import { queryOptions, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, ApiError, unwrap } from '~/lib/api'
import { sessionQuery } from '~/lib/session'

// Compensation is club-wide (contracts span teams), so keys carry the season, not a team. All keys
// start with 'compensation': the server's invalidate(['compensation']) refreshes every screen here.
export const compKeys = {
  all: ['compensation'] as const,
  season: (season: string) => ['compensation', season, 'season'] as const,
  month: (month: string) => ['compensation', 'month', month] as const,
  personMonth: (month: string, personId: string) => ['compensation', 'month', month, personId] as const,
  contracts: (season: string) => ['compensation', season, 'contracts'] as const,
  contract: (id: string) => ['compensation', 'contract', id] as const,
  types: ['compensation', 'types'] as const,
  rules: ['compensation', 'rules'] as const,
  person: (personId: string, season: string) => ['compensation', season, 'person', personId] as const,
}

type Data<F extends (...a: never[]) => unknown> = Awaited<ReturnType<F>>

const fetchSeason = (season: string) => unwrap(api.compensation.season.$get({ query: { season } }))
export const seasonQuery = (season: string) => queryOptions({ queryKey: compKeys.season(season), queryFn: () => fetchSeason(season) })
export type SeasonData = Data<typeof fetchSeason>
export type SeasonPerson = SeasonData['people'][number]

/**
 * The month to close next: the first finished month not fully paid, else the last finished one.
 * Overview button and the wizard's default use the same, so neither offers the running month.
 */
export function closeTarget(data: SeasonData | undefined): string | undefined {
  const finished = data?.months.filter((m) => m.finished) ?? []
  return (finished.find((m) => m.status !== 'paid') ?? finished.at(-1))?.month
}

const fetchMonth = (month: string) => unwrap(api.compensation.months[':month'].$get({ param: { month } }))
export const monthQuery = (month: string) => queryOptions({ queryKey: compKeys.month(month), queryFn: () => fetchMonth(month) })
export type MonthData = Data<typeof fetchMonth>
export type MonthRow = MonthData['rows'][number]
export type Anomaly = MonthData['anomalies'][number]

const fetchPersonMonth = (month: string, personId: string) =>
  unwrap(api.compensation.months[':month'].people[':personId'].$get({ param: { month, personId } }))
export const personMonthQuery = (month: string, personId: string) =>
  queryOptions({ queryKey: compKeys.personMonth(month, personId), queryFn: () => fetchPersonMonth(month, personId) })
export type PersonMonth = Data<typeof fetchPersonMonth>
export type Line = PersonMonth['lines'][number]

const fetchContracts = (season: string) => unwrap(api.compensation.contracts.$get({ query: { season } }))
export const contractsQuery = (season: string) => queryOptions({ queryKey: compKeys.contracts(season), queryFn: () => fetchContracts(season) })
export type ContractRow = Data<typeof fetchContracts>['rows'][number]

const fetchContract = (id: string) => unwrap(api.compensation.contracts[':id'].$get({ param: { id } }))
export const contractQuery = (id: string) => queryOptions({ queryKey: compKeys.contract(id), queryFn: () => fetchContract(id) })
export type ContractDetail = Data<typeof fetchContract>
export type ContractItem = ContractDetail['items'][number]

const fetchTypes = () => unwrap(api.compensation.types.$get())
export const typesQuery = () => queryOptions({ queryKey: compKeys.types, queryFn: fetchTypes })
export type CompType = Data<typeof fetchTypes>[number]

const fetchCandidates = () => unwrap(api.compensation.candidates.$get())
export const candidatesQuery = () => queryOptions({ queryKey: ['compensation', 'candidates'] as const, queryFn: fetchCandidates })
export type Candidate = Data<typeof fetchCandidates>[number]

const fetchRules = () => unwrap(api.compensation.rules.$get())
export const rulesQuery = () => queryOptions({ queryKey: compKeys.rules, queryFn: fetchRules })
export type Rules = Data<typeof fetchRules>
/** The club's bench mode: it decides what the bench units mean and how they are labelled. */
export const useBenchMode = () => useQuery(rulesQuery()).data?.benchMode ?? 'whole_bench'

const fetchPerson = (personId: string, season: string) =>
  unwrap(api.compensation.people[':personId'].$get({ param: { personId }, query: { season } }))
export const personQuery = (personId: string, season: string) =>
  queryOptions({ queryKey: compKeys.person(personId, season), queryFn: () => fetchPerson(personId, season) })
export type PersonCompensationData = Data<typeof fetchPerson>

/** Season key for loaders outside React (same storage key as useScope). */
export async function loaderSeason(qc: QueryClient) {
  const session = await qc.ensureQueryData(sessionQuery)
  const active = session.active
  if (!active) return null
  let stored: string | null = null
  try {
    stored = localStorage.getItem(`sc-season:${active.organization.id}`)
  } catch {
    // private mode
  }
  return {
    season: active.seasons.find((s) => s.key === stored)?.key ?? active.currentSeason,
    canRead: active.permissions.includes('finance.read'),
    timezone: active.organization.timezone,
  }
}

function useRefresh(...extra: string[][]) {
  const qc = useQueryClient()
  return () => Promise.all([['compensation'], ...extra].map((queryKey) => qc.invalidateQueries({ queryKey })))
}

// --- month close ----------------------------------------------------------------------------

export function useComputeMonth(month: string) {
  const refresh = useRefresh()
  return useMutation({ mutationFn: () => unwrap(api.compensation.months[':month'].compute.$post({ param: { month } })), onSuccess: refresh })
}

export function useApproveMonth(month: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: { personIds?: string[]; expectedTotalCents?: number }) =>
      unwrap(api.compensation.months[':month'].approve.$post({ param: { month }, json })),
    onSuccess: refresh,
  })
}

export function usePayMonth(month: string) {
  const refresh = useRefresh(['finance'])
  return useMutation({
    mutationFn: (json: { personIds?: string[]; paidOn: string; budgetId?: string | null }) =>
      unwrap(api.compensation.months[':month'].pay.$post({ param: { month }, json })),
    onSuccess: refresh,
  })
}

export function useVoidSettlement() {
  const refresh = useRefresh(['finance'])
  return useMutation({
    mutationFn: (v: { id: string; reason: string; recompute: boolean }) =>
      unwrap(api.compensation.settlements[':id'].void.$post({ param: { id: v.id }, json: { reason: v.reason, recompute: v.recompute } })),
    onSuccess: refresh,
  })
}

// --- contracts ------------------------------------------------------------------------------

type ContractCreate = Parameters<typeof api.compensation.contracts.$post>[0]['json']
type ContractPatch = Parameters<(typeof api.compensation.contracts)[':id']['$patch']>[0]['json']
export type ItemInput = ContractCreate['items'][number]

export function useCreateContract() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (json: ContractCreate) => unwrap(api.compensation.contracts.$post({ json })), onSuccess: refresh })
}

export function useUpdateContract(id: string) {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (json: ContractPatch) => unwrap(api.compensation.contracts[':id'].$patch({ param: { id }, json })), onSuccess: refresh })
}

export function useContractAction(id: string) {
  const refresh = useRefresh()
  const c = api.compensation.contracts[':id']
  return useMutation({
    mutationFn: async (a: { kind: 'activate' } | { kind: 'end'; lastDay: string } | { kind: 'delete' } | { kind: 'version'; from: string; items?: ItemInput[] } | { kind: 'sign'; image: string }): Promise<{ id?: string; deleted?: boolean; restored?: string | null; signedAt?: string }> => {
      const param = { id }
      switch (a.kind) {
        case 'activate':
          return unwrap(c.activate.$post({ param }))
        case 'end':
          return unwrap(c.end.$post({ param, json: { lastDay: a.lastDay } }))
        case 'delete':
          return unwrap(c.$delete({ param }))
        case 'version':
          return unwrap(c.version.$post({ param, json: { from: a.from, ...(a.items ? { items: a.items } : {}) } }))
        case 'sign':
          return unwrap(c.signature.$post({ param, json: { image: a.image } }))
      }
    },
    onSuccess: refresh,
  })
}

export function useSimulate() {
  return useMutation({
    mutationFn: (json: Parameters<typeof api.compensation.contracts.simulate.$post>[0]['json']) => unwrap(api.compensation.contracts.simulate.$post({ json })),
    // The preview is best effort; a failed one just shows nothing.
    onError: () => {},
  })
}

export function useRollover() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: { season: string; dryRun: boolean }) => unwrap(api.compensation.contracts.rollover.$post({ json })),
    onSuccess: (data) => (data.dryRun ? undefined : refresh()),
  })
}

export type ImportResult = {
  ok: boolean
  message: string | null
  dryRun: boolean
  counts: { create: number; update: number; unchanged: number; error: number }
  rows: { row: number; name: string; action: 'create' | 'update' | 'unchanged' | 'error'; message?: string; changes: string[] }[]
}

export function useImportContracts() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: async (v: { file: File; dryRun: boolean }) => {
      const form = new FormData()
      form.set('file', v.file)
      let res: Response
      try {
        res = await fetch(`/api/compensation/contracts/import?dryRun=${v.dryRun ? '1' : '0'}`, {
          method: 'POST',
          body: form,
          credentials: 'same-origin',
          headers: { 'X-SquadCore': '1' },
        })
      } catch {
        throw new ApiError(0, 'network', 'Keine Verbindung. Prüfe dein Netz und versuch es erneut.')
      }
      const body = (await res.json().catch(() => ({}))) as ImportResult & { error?: { message?: string } }
      if (!res.ok) throw new ApiError(res.status, 'import', body.error?.message ?? 'Der Import ist fehlgeschlagen.')
      return body
    },
    onSuccess: (data) => (data.dryRun ? undefined : refresh()),
  })
}

// --- types and rules ------------------------------------------------------------------------

type TypeCreate = Parameters<typeof api.compensation.types.$post>[0]['json']
type TypePatch = Parameters<(typeof api.compensation.types)[':id']['$patch']>[0]['json']

export function useSaveType() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (v: { id?: string; json: TypeCreate | TypePatch }) =>
      v.id
        ? unwrap(api.compensation.types[':id'].$patch({ param: { id: v.id }, json: v.json }))
        : unwrap(api.compensation.types.$post({ json: v.json as TypeCreate })),
    onSuccess: refresh,
  })
}

export function useDeleteType() {
  const refresh = useRefresh()
  return useMutation({ mutationFn: (id: string) => unwrap(api.compensation.types[':id'].$delete({ param: { id } })), onSuccess: refresh })
}

export function useAssignType(id: string) {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: { rateCents?: number; scope: 'players' | 'all'; dryRun: boolean }) =>
      unwrap(api.compensation.types[':id'].assign.$post({ param: { id }, json })),
    onSuccess: (data) => (data.dryRun ? undefined : refresh()),
  })
}

export function useSaveRules() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: (json: Parameters<typeof api.compensation.rules.$patch>[0]['json']) => unwrap(api.compensation.rules.$patch({ json })),
    onSuccess: refresh,
  })
}

/** What a rule change would do to the open months, before it is saved. */
export const previewRules = (json: Parameters<typeof api.compensation.rules.$patch>[0]['json']) => unwrap(api.compensation.rules.preview.$post({ json }))

// --- links ----------------------------------------------------------------------------------

export const sheetPdf = (month: string, personId: string) => `/api/compensation/months/${month}/people/${personId}/pdf`
export const reportPdf = (month: string) => `/api/compensation/months/${month}/report.pdf`
export const contractPdf = (id: string) => `/api/compensation/contracts/${id}/pdf`
export const contractsXlsx = '/api/compensation/contracts/export.xlsx'
export const templateXlsx = '/api/compensation/contracts/template.xlsx'
